// API client for Cloudflare Workers endpoints

import { apiUrl } from './env'
import type { SessionResponse, ChunkUploadResponse, FinalizeResponse } from '@/types'

// Separate keys for feed (upvotes/comments) vs recording
const FEED_SESSION_KEY = 'philly_watch_feed_session'
const RECORDING_SESSION_KEY = 'philly_watch_recording_session'
const SESSION_EXPIRY_HOURS = 24

interface StoredSession {
  token: string
  createdAt: number
}

// Track pending chunk uploads for reliability
interface PendingUpload {
  chunk: Blob
  chunkNumber: number
  retries: number
}

class ApiClient {
  private sessionToken: string | null = null
  private isRecordingSession: boolean = false
  private pendingUploads: Map<number, PendingUpload> = new Map()
  private successfulUploads: Set<number> = new Set()

  constructor() {
    // Try to restore feed session from localStorage on init
    this.restoreSession(FEED_SESSION_KEY)
  }

  /**
   * Restore session from localStorage if valid
   */
  private restoreSession(storageKey: string): void {
    try {
      const stored = localStorage.getItem(storageKey)
      if (!stored) return

      const session: StoredSession = JSON.parse(stored)
      const ageHours = (Date.now() - session.createdAt) / (1000 * 60 * 60)

      if (ageHours < SESSION_EXPIRY_HOURS) {
        this.sessionToken = session.token
      } else {
        // Session expired, clear it
        localStorage.removeItem(storageKey)
      }
    } catch {
      // Invalid stored data, clear it
      localStorage.removeItem(storageKey)
    }
  }

  /**
   * Save session to localStorage
   */
  private saveSession(token: string, storageKey: string): void {
    const session: StoredSession = {
      token,
      createdAt: Date.now()
    }
    localStorage.setItem(storageKey, JSON.stringify(session))
  }

  /**
   * Create a new session from the server
   */
  private async fetchNewSession(): Promise<string> {
    const response = await fetch(`${apiUrl}/session`, {
      method: 'POST',
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create session' }))
      throw new Error(error.error || 'Failed to create session')
    }

    const data: SessionResponse = await response.json()
    return data.sessionToken
  }

  /**
   * Get or create an anonymous session for feed interactions (upvotes/comments)
   * This session persists across page refreshes
   */
  async createSession(): Promise<string> {
    // Return existing session if we have one (for feed)
    if (this.sessionToken && !this.isRecordingSession) {
      return this.sessionToken
    }

    const token = await this.fetchNewSession()
    this.sessionToken = token
    this.isRecordingSession = false
    this.saveSession(token, FEED_SESSION_KEY)
    return token
  }

  /**
   * Create a FRESH session specifically for recording
   * Always creates a new session to ensure clean state
   */
  async createRecordingSession(): Promise<string> {
    // Always create a fresh session for recording
    const token = await this.fetchNewSession()
    this.sessionToken = token
    this.isRecordingSession = true
    this.pendingUploads.clear()
    this.successfulUploads.clear()
    this.saveSession(token, RECORDING_SESSION_KEY)
    return token
  }

  /**
   * Upload a video chunk with retry logic
   */
  async uploadChunk(chunk: Blob, chunkNumber: number, maxRetries = 3): Promise<ChunkUploadResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - call createRecordingSession first')
    }

    // Track this upload
    this.pendingUploads.set(chunkNumber, { chunk, chunkNumber, retries: 0 })

    let lastError: Error | null = null

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const formData = new FormData()
        formData.append('chunk', chunk)
        formData.append('chunkNumber', String(chunkNumber).padStart(3, '0'))

        const response = await fetch(`${apiUrl}/chunk`, {
          method: 'POST',
          headers: {
            'X-Session-Token': this.sessionToken,
          },
          body: formData,
        })

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to upload chunk' }))
          throw new Error(error.error || 'Failed to upload chunk')
        }

        const result = await response.json()

        // Mark as successful
        this.successfulUploads.add(chunkNumber)
        this.pendingUploads.delete(chunkNumber)

        return result
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Upload failed')

        // Update retry count
        const pending = this.pendingUploads.get(chunkNumber)
        if (pending) {
          pending.retries = attempt + 1
        }

        // Wait before retry (exponential backoff)
        if (attempt < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000))
        }
      }
    }

    throw lastError || new Error('Failed to upload chunk after retries')
  }

  /**
   * Check if all chunks have been uploaded successfully
   */
  hasAllChunksUploaded(): boolean {
    return this.pendingUploads.size === 0 && this.successfulUploads.size > 0
  }

  /**
   * Get upload status
   */
  getUploadStatus(): { successful: number; pending: number; failed: number } {
    const failed = Array.from(this.pendingUploads.values()).filter(u => u.retries >= 3).length
    return {
      successful: this.successfulUploads.size,
      pending: this.pendingUploads.size - failed,
      failed
    }
  }

  /**
   * Wait for all pending uploads to complete (with timeout)
   */
  async waitForUploads(timeoutMs = 30000): Promise<boolean> {
    const startTime = Date.now()

    while (this.pendingUploads.size > 0) {
      if (Date.now() - startTime > timeoutMs) {
        console.warn('Upload wait timeout - some chunks may be pending')
        return false
      }
      await new Promise(resolve => setTimeout(resolve, 500))
    }

    return true
  }

  /**
   * Emergency finalize - uses sendBeacon for reliability when page is closing
   * This is fire-and-forget, used when the user can't press stop (phone taken, etc.)
   */
  emergencyFinalize(options?: { gpsLat?: number; gpsLng?: number; fileExtension?: string }): boolean {
    if (!this.sessionToken || !this.isRecordingSession) {
      return false
    }

    // Only attempt if we have at least one successful upload
    if (this.successfulUploads.size === 0) {
      return false
    }

    const payload = JSON.stringify({
      sessionToken: this.sessionToken,
      emergency: true, // Flag for server to know this was auto-saved
      fileExtension: options?.fileExtension || 'webm',
      gpsLat: options?.gpsLat,
      gpsLng: options?.gpsLng,
    })

    // sendBeacon is designed for this - reliable even when page is closing
    const success = navigator.sendBeacon(
      `${apiUrl}/finalize`,
      new Blob([payload], { type: 'application/json' })
    )

    if (success) {
      console.log('Emergency finalize beacon sent')
      // Clear the recording session since we've attempted to save
      this.isRecordingSession = false
      localStorage.removeItem(RECORDING_SESSION_KEY)
    }

    return success
  }

  /**
   * Finalize the video upload
   */
  async finalizeVideo(options?: {
    gpsLat?: number
    gpsLng?: number
    tags?: string[]
    fileExtension?: string
  }): Promise<FinalizeResponse> {
    if (!this.sessionToken) {
      throw new Error('No session token - call createSession first')
    }

    const response = await fetch(`${apiUrl}/finalize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionToken: this.sessionToken,
        fileExtension: options?.fileExtension || 'webm',
        gpsLat: options?.gpsLat,
        gpsLng: options?.gpsLng,
        tags: options?.tags,
      }),
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to finalize video' }))
      throw new Error(error.error || 'Failed to finalize video')
    }

    return response.json()
  }

  /**
   * Check API health
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(`${apiUrl}/health`)
      return response.ok
    } catch {
      return false
    }
  }

  /**
   * Get current session token
   */
  getSessionToken(): string | null {
    return this.sessionToken
  }

  /**
   * Clear recording session (keeps feed session intact)
   */
  clearRecordingSession(): void {
    if (this.isRecordingSession) {
      this.sessionToken = null
      this.isRecordingSession = false
      this.pendingUploads.clear()
      this.successfulUploads.clear()
      localStorage.removeItem(RECORDING_SESSION_KEY)
      // Restore feed session if available
      this.restoreSession(FEED_SESSION_KEY)
    }
  }

  /**
   * Clear all sessions (full reset)
   */
  clearSession(): void {
    this.sessionToken = null
    this.isRecordingSession = false
    this.pendingUploads.clear()
    this.successfulUploads.clear()
    localStorage.removeItem(FEED_SESSION_KEY)
    localStorage.removeItem(RECORDING_SESSION_KEY)
  }
}

// Export singleton instance
export const apiClient = new ApiClient()
